/**
 * INSTÄLLNINGARNA — skärmen bakom kugghjulet i toppradens högra hörn.
 *
 * PROTOTYPEN HAR INGEN. Kugghjulet finns där (appboken.html rad 455-457) men
 * leder ingenstans; hela genomgången är de tio skärmarna. Den här skärmen är
 * därför byggd på PRODUKTENS behov och inte på ett facit: uppgiften var "i den
 * mån de behövs för att appen ska gå att använda: vem är jag, vilken firma,
 * logga ut". Den svarar på exakt de tre frågorna — plus en fjärde som hör till
 * KONTOT och inte till firman: att lägga upp en egen firma. Raden är den enda
 * vägen till skärmen 'nyfirma' för den som redan är med någonstans, för världen
 * 'ingen_firma' (App.tsx) visas bara för den som inte är med i någon firma alls
 * — och en inhyrd (beslut 17) som vill starta eget är inte den.
 *
 * DEN VISAR, DEN ÄNDRAR INTE. Att byta namn (UPDATE_ACCOUNT), byta firmanamn
 * (UPDATE_COMPANY) och hantera prenumerationen (BILLING_PORTAL) står i
 * kontraktet men är inte byggt här — se LUCKOR i överlämningen. Det enda som
 * FÅR ändras härifrån är vilken firma som är aktiv, och det är ett val i
 * telefonen och ingen skrivning mot servern.
 *
 * ETT UNDANTAG, OCH DET LEDER BORT HÄRIFRÅN: raden "Radera firman" längst ner
 * (villkoren punkt 10.7). Den skriver ingenting själv — den öppnar
 * bekräftelsevyn RaderaFirma.tsx — och den finns bara för ÄGAREN. För alla
 * andra renderas den inte alls.
 *
 * LÅSLÄGET SYNS HÄR OCH INGEN ANNANSTANS. Beslut 11: en pausad prenumeration
 * stänger skrivningen men aldrig läsningen eller exporten. Står firman i
 * 'locked' ska chefen få VETA det på ett ställe, med rakt språk, i stället för
 * att möta ett SUBSCRIPTION_LOCKED nästa gång han trycker Spara.
 */

import {
  Bricka,
  Installningsrad,
  Knapp,
  Listrad,
  Rubrik,
  Tomtext,
  Yta,
} from '../ui';
import { useInloggad, useSession } from '../app/session';
import { useNavigering } from '../app/navigering';
import { PRENUMERATIONSNAMN, ROLLNAMN } from './oversikt';

/** '5595958553' -> '559595-8553'. Så skriver man ett org.nr för en människa. */
const visaOrgnummer = (orgnummer: string): string =>
  orgnummer.length === 10 ? `${orgnummer.slice(0, 6)}-${orgnummer.slice(6)}` : orgnummer;

export const Installningar = () => {
  const { konto, aktiv, firmor, roll, arAgare, arLast, kanSeLaget } = useInloggad();
  const { bytFirma, loggaUt } = useSession();
  const { ga } = useNavigering();

  return (
    <>
      <Rubrik titel={konto.name} under={konto.email} />

      <Yta rubrik="Firman" rakning={arAgare ? 'du äger den' : undefined}>
        <Installningsrad etikett="Firma" varde={aktiv.name} />
        <Installningsrad etikett="Org.nr" varde={visaOrgnummer(aktiv.org_number)} />
        <Installningsrad etikett="Du är" varde={ROLLNAMN[roll]} />
        {/*
          PRENUMERATIONEN STÅR PÅ ETT STÄLLE ÅT GÅNGEN. Den som har full vy får
          den klickbara raden längre ner — samma uppgift, men med vägen till
          skärmen. Den som inte har det (montör, lärling, inhyrd) når inte
          skärmen alls och får därför läget som ren visningsrad, precis som förut.
        */}
        {!kanSeLaget && (
          <Installningsrad
            etikett="Prenumeration"
            varde={PRENUMERATIONSNAMN[aktiv.subscription_status]}
            atgard={
              arLast ? <Bricka ton="pagaende">pausad</Bricka> : undefined
            }
          />
        )}
        {arLast && (
          <Tomtext>
            Allt går att läsa och exportera som vanligt. Det som är stängt är att
            spara nytt — det du redan skrivit in ligger kvar.
          </Tomtext>
        )}

        {/*
          FIRMANS STAMDATA. Fyra skärmar som alla skriver på MANAGER_ONLY-rutter
          och därför står i läge 'lag' (app/skarmar.ts). Raderna är dolda för den
          som inte har full vy: en montör har dem inte i sin skärmkarta, och en
          rad som leder till startsidan är värre än ingen rad alls. Bokföraren
          ser dem, når skärmarna och möts där av ett besked i stället för fält
          (beslut 07).

          MOMENTEN LIGGER FÖRST med flit: utan ett enda moment går inte en enda
          timme att rapportera, så det är den första saken en ny firma måste göra.
        */}
        {kanSeLaget && (
          <>
            <Listrad
              titel="Moment"
              under="Texterna som hamnar på kundens faktura"
              onClick={() => ga('moment')}
            />
            <Listrad
              titel="Kunder"
              under="Adresser och ROT-satsen som fryses på varje timme"
              onClick={() => ga('kunder')}
            />
            <Listrad
              titel="Timpriser"
              under="Kronor per timme och roll, ex moms"
              onClick={() => ga('timpriser')}
            />
            <Listrad
              titel="Personal"
              under="Bjud in, byt roll och ta bort"
              onClick={() => ga('personal')}
            />
            <Listrad
              titel="Prenumeration"
              under={PRENUMERATIONSNAMN[aktiv.subscription_status]}
              onClick={() => ga('prenumeration')}
            />
          </>
        )}
      </Yta>

      {/*
        FIRMAVALET. Ett konto kan vara med i flera firmor (en inhyrd är det
        alltid). Valet ligger i telefonen och är ingen skrivning; rollen och
        vad som syns följer sedan av ME för DEN firman.
      */}
      {firmor.length > 1 && (
        <Yta rubrik="Byt firma" rakning={`${firmor.length} st`}>
          {firmor.map((f) => (
            <Listrad
              key={f.id}
              titel={f.name}
              under={ROLLNAMN[f.role]}
              varde={f.id === aktiv.id ? 'vald' : undefined}
              onClick={f.id === aktiv.id ? undefined : () => bytFirma(f.id)}
            />
          ))}
        </Yta>
      )}

      <Yta rubrik="Kontot">
        <Listrad
          titel="Mitt konto"
          under="Namn, e-post och den här telefonen"
          onClick={() => ga('profil')}
        />
        {/*
          FJÄRDE FRÅGAN, och den hör till KONTOT och inte till firman ovanför:
          CREATE_COMPANY har auth 'account' och NO_ROLES. Raden är dessutom den
          ENDA vägen till skärmen för den som redan är med någonstans — världen
          'ingen_firma' (App.tsx) visas bara för den som inte är med i någon
          firma alls, och en inhyrd (beslut 17) som driver eget vid sidan av är
          inte den.
        */}
        <Listrad
          titel="Lägg upp en egen firma"
          under="Du blir ägare, med en provperiod på sju dagar"
          onClick={() => ga('nyfirma')}
        />
      </Yta>

      <Knapp variant="stor-tyst" onClick={loggaUt}>
        Logga ut
      </Knapp>

      <Tomtext>
        Klartid loggar in med en kod i mejlen — det finns inget lösenord att
        glömma. Loggar du ut får du en ny kod nästa gång.
      </Tomtext>

      {/*
        RADERINGEN — LÄNGST NER, TYST, OCH BARA FÖR ÄGAREN. Villkoren punkt 10.7
        lovar raden ordagrant: "Under Inställningar finns 'Radera firman'."
        DELETE_COMPANY kräver ägaren (requires_owner), och för alla andra — en
        chef som inte äger firman, en bokförare, en montör — finns raden inte:
        den är inte gömd, den renderas inte. Ett gränssnitt som erbjuder något
        och sedan får nej är sämre än ett som aldrig erbjuder.
        Ingen röd knapp: en tyst yta med egen rubrik och ett litet piller. Allt
        som ska sägas — namn, org.nr, följderna, exporten — står på nästa skärm,
        och ingenting händer förrän numret är skrivet där.
      */}
      {arAgare && (
        <Yta tyst rubrik="Radera firman">
          <Installningsrad
            etikett="Tar bort"
            varde={aktiv.name}
            atgard={
              <Knapp variant="piller-mini" onClick={() => ga('raderafirma')}>
                Radera ...
              </Knapp>
            }
          />
          <Tomtext>
            Firman och allt i den försvinner för alltid. Du bekräftar med firmans
            organisationsnummer på nästa sida — ingenting händer förrän dess.
          </Tomtext>
        </Yta>
      )}
    </>
  );
};
